import React, { useState } from 'react';
import { Phone, Mail, Globe, MapPin, Clock, ArrowRight, Loader2, Instagram, Facebook, Linkedin, Youtube } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SectionBubbles from '../components/SectionBubbles';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    city: '',
    fleetSize: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: formData.companyName,
          contact_name: formData.contactName,
          email: formData.email,
          phone: formData.phone,
          city: formData.city,
          fleet_size: formData.fleetSize,
          message: formData.message
        })
      });
      if (!res.ok) throw new Error('Submission failed');
      toast.success("Thank you for your interest. We'll contact you within 24 hours.");
      setFormData({ companyName: '', contactName: '', email: '', phone: '', city: '', fleetSize: '', message: '' });
    } catch {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="page-wrapper">
      <SEO 
        title={t('seo.contact.title')}
        description={t('seo.contact.description')}
        keywords={t('seo.contact.keywords')}
        canonical="https://dsotocleaning.com/contact"
      />
      
      <Navigation />
      
      <section className="page-hero">
        <div className="container">
          <h1 className="page-hero-title">{t('contact.hero_title')}</h1>
          <p className="page-hero-description">{t('contact.hero_desc')}</p>
        </div>
      </section>

      <section className="section-padding">
        <SectionBubbles />
        <div className="container">
          <div className="contact-page-grid">
            <div className="contact-info-section">
              <h2 className="section-title">Corporate Headquarters</h2>
              <p className="section-description mb-8">
                Serving corporate fleet operators and rental companies with professional 
                detailing solutions across multiple markets.
              </p>

              <div className="contact-details-list">
                <div className="contact-detail-item">
                  <div className="contact-detail-icon">
                    <Phone />
                  </div>
                  <div className="contact-detail-content">
                    <div className="contact-detail-label">Phone</div>
                    <a href="tel:4079461710" className="contact-detail-value">407-946-1710</a>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-detail-icon">
                    <Mail />
                  </div>
                  <div className="contact-detail-content">
                    <div className="contact-detail-label">Email</div>
                    <a href="mailto:Info@dsotocleaning.com" className="contact-detail-value">Info@dsotocleaning.com</a>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-detail-icon">
                    <Globe />
                  </div>
                  <div className="contact-detail-content">
                    <div className="contact-detail-label">Website</div>
                    <a href="https://dsotocleaning.com" target="_blank" rel="noopener noreferrer" className="contact-detail-value">
                      dsotocleaning.com
                    </a>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-detail-icon">
                    <MapPin />
                  </div>
                  <div className="contact-detail-content">
                    <div className="contact-detail-label">Service Areas</div>
                    <div className="contact-detail-value">Three-City Operations</div>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-detail-icon">
                    <Clock />
                  </div>
                  <div className="contact-detail-content">
                    <div className="contact-detail-label">Operating Hours</div>
                    <div className="contact-detail-value">
                      24 Hours a Day | 7 Days a Week
                    </div>
                  </div>
                </div>
              </div>

              <div className="contact-social" data-testid="contact-social">
                <div className="contact-detail-label" style={{ marginBottom: '0.75rem' }}>Follow Us</div>
                <div className="contact-social-links">
                  <a href="https://www.instagram.com/dsotocleaningservice/" target="_blank" rel="noopener noreferrer" className="contact-social-link" aria-label="Instagram" data-testid="contact-social-instagram"><Instagram size={22} /></a>
                  <a href="https://www.facebook.com/profile.php?id=61588268131598" target="_blank" rel="noopener noreferrer" className="contact-social-link" aria-label="Facebook" data-testid="contact-social-facebook"><Facebook size={22} /></a>
                  <a href="https://www.linkedin.com/company/d-soto-cleaning/posts/?feedView=all" target="_blank" rel="noopener noreferrer" className="contact-social-link" aria-label="LinkedIn" data-testid="contact-social-linkedin"><Linkedin size={22} /></a>
                  <a href="https://www.youtube.com/@DSotoCleaningServices" target="_blank" rel="noopener noreferrer" className="contact-social-link" aria-label="YouTube" data-testid="contact-social-youtube"><Youtube size={22} /></a>
                </div>
              </div>
            </div>

            <div className="contact-form-section">
              <div className="contact-form-card">
                <h3 className="contact-form-title">Request Corporate Consultation</h3>
                <p className="contact-form-description">
                  Complete the form below and our corporate team will contact you within 24 hours.
                </p>
                <form onSubmit={handleSubmit} className="contact-page-form">
                  <Input
                    name="companyName"
                    placeholder="Company Name *"
                    value={formData.companyName}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                  <Input
                    name="contactName"
                    placeholder="Contact Name *"
                    value={formData.contactName}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                  <Input
                    name="email"
                    type="email"
                    placeholder="Email Address *"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                  <Input
                    name="phone"
                    type="tel"
                    placeholder="Phone Number *"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                  <Input
                    name="city"
                    placeholder="City / Location"
                    value={formData.city}
                    onChange={handleChange}
                    className="form-input"
                  />
                  <Input
                    name="fleetSize"
                    placeholder="Fleet Size (approximate)"
                    value={formData.fleetSize}
                    onChange={handleChange}
                    className="form-input"
                  />
                  <Textarea
                    name="message"
                    placeholder="Tell us about your fleet detailing needs..."
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className="form-input"
                  />
                  <Button type="submit" size="lg" className="submit-button-full" disabled={isSubmitting} data-testid="contact-submit-btn">
                    {isSubmitting ? <><Loader2 className="mr-2 animate-spin" size={18} /> Sending...</> : <>Send Inquiry <ArrowRight className="ml-2" /></>}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="map-section">
        <div className="map-placeholder">
          <div className="map-overlay">
            <h3>Serving Major Metropolitan Areas</h3>
            <p>Corporate fleet operations across three cities</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
